import { useRef, useState } from "react";
import { toast } from "sonner";
import { Mic, Square, Play, Pause, Loader2, X } from "lucide-react";
import { audioSrc } from "@/lib/api";

// Compact record / play / clear control for a single audio clip (returns the blob to `onRecorded`).
export const CueRecorder = ({ audio, onRecorded, onClear, testid }) => {
  const [recording, setRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const elRef = useRef(null);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setUploading(true);
        try { await onRecorded(new Blob(chunksRef.current, { type: "audio/webm" })); } catch { toast.error("Could not save recording"); } finally { setUploading(false); }
      };
      mediaRef.current = mr; mr.start(); setRecording(true);
    } catch { toast.error("Microphone access was blocked. Allow mic access and try again."); }
  };
  const stop = () => { mediaRef.current?.stop(); setRecording(false); };
  const toggle = () => {
    if (!audio) return;
    if (!elRef.current) elRef.current = new Audio();
    if (playing) { elRef.current.pause(); setPlaying(false); return; }
    elRef.current.src = audioSrc(audio); elRef.current.onended = () => setPlaying(false); elRef.current.play(); setPlaying(true);
  };
  const btn = "h-7 px-2.5 rounded-full text-[11px] border flex items-center gap-1";
  return (
    <div className="flex items-center gap-1" data-testid={`${testid}-recorder`}>
      {uploading ? <span className={`${btn} border-white/10 text-white/50`}><Loader2 className="w-3 h-3 animate-spin" /> Saving</span>
        : recording ? <button type="button" data-testid={`${testid}-stop`} onClick={stop} className={`${btn} border-red-400/50 bg-red-500/20 text-red-200`}><Square className="w-3 h-3" /> Stop</button>
        : <button type="button" data-testid={`${testid}-record`} onClick={start} className={`${btn} ${audio ? "border-white/10 text-white/50 hover:text-white" : "border-emerald-400/40 text-emerald-200"}`}><Mic className="w-3 h-3" /> {audio ? "Re-record" : "Voiceover"}</button>}
      {audio && !recording && <>
        <button type="button" data-testid={`${testid}-play`} onClick={toggle} className={`${btn} border-white/10 text-white/70`}>{playing ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}</button>
        <button type="button" data-testid={`${testid}-clear`} onClick={onClear} title="Remove voiceover (Echo will use the AI voice)" className="h-7 w-7 rounded-full hover:bg-white/10 text-white/40 flex items-center justify-center"><X className="w-3 h-3" /></button>
      </>}
    </div>
  );
};
